import { categoryTaxonomyNodeId, isReservedCategoryGroupSlug } from "../../shared/taxonomy";
import { CATEGORY_GROUPS, getCategoryProfile } from "./category-profiles";
import type { IntakeCategoryOption } from "./category-profiles";
import type { IntakeCategoryId } from "./types";

export const CATEGORY_TAXONOMY_VERSION_ID = "category-taxonomy-v1";

export type CategoryTaxonomyStatus = "draft" | "published";
export type CategoryTaxonomyNodeKind = "group" | "category";
export type CategoryTaxonomyNodeStatus = "active" | "hidden" | "deprecated";

export type CategoryTaxonomyNode = {
  id: string;
  kind: CategoryTaxonomyNodeKind;
  label: string;
  description: string;
  slug: string;
  order: number;
  status: CategoryTaxonomyNodeStatus;
  parentId?: string;
  profileId?: Exclude<IntakeCategoryId, "">;
  aliases?: string[];
  examples?: string[];
};

export type CategoryTaxonomyConfig = {
  versionId: string;
  status: CategoryTaxonomyStatus;
  nodes: CategoryTaxonomyNode[];
};

export type CategoryTaxonomyDiagnostic = {
  level: "error" | "warning";
  code: string;
  nodeId?: string;
  message: string;
};

export type TaxonomyCategoryOption = IntakeCategoryOption & {
  nodeId: string;
  path: string;
  status: CategoryTaxonomyNodeStatus;
};

export type TaxonomyCategoryGroup = {
  id: string;
  label: string;
  description: string;
  nodeId: string;
  slug: string;
  status: CategoryTaxonomyNodeStatus;
  options: TaxonomyCategoryOption[];
};

const DEFAULT_GROUP_NODES: CategoryTaxonomyNode[] = CATEGORY_GROUPS.map((group, order) => ({
  id: groupNodeId(group.id),
  kind: "group",
  label: group.label,
  description: group.description,
  slug: group.id,
  order,
  status: "active",
}));

const DEFAULT_CATEGORY_NODES: CategoryTaxonomyNode[] = CATEGORY_GROUPS.flatMap((group) => group.options.map((option, order) => {
  const mergedBeautyCategory = option.id === "beauty_nail";
  const hiddenMergedBeautyCategory = option.id === "beauty_semipermanent" || option.id === "beauty_other";
  return {
    id: categoryNodeId(option.id),
    kind: "category" as const,
    parentId: groupNodeId(group.id),
    profileId: option.id,
    label: mergedBeautyCategory ? "네일 · 반영구 · 기타 뷰티" : option.label,
    description: mergedBeautyCategory ? "네일, 반영구, 속눈썹, 왁싱처럼 뷰티 세부 관리를 제공하는 파트너" : option.description,
    examples: mergedBeautyCategory ? ["네일", "반영구", "속눈썹", "왁싱", "기타 뷰티 관리"] : option.examples,
    slug: defaultCategorySlug(option.id),
    aliases: mergedBeautyCategory ? ["semipermanent", "other"] : [],
    order,
    status: hiddenMergedBeautyCategory ? "deprecated" as const : "active" as const,
  };
}));

export const DEFAULT_CATEGORY_TAXONOMY_CONFIG: CategoryTaxonomyConfig = {
  versionId: CATEGORY_TAXONOMY_VERSION_ID,
  status: "published",
  nodes: [...DEFAULT_GROUP_NODES, ...DEFAULT_CATEGORY_NODES],
};

export function groupNodeId(groupId: string): string {
  return `group.${groupId}`;
}

export function categoryNodeId(categoryId: Exclude<IntakeCategoryId, "">): string {
  return categoryTaxonomyNodeId(categoryId);
}

export function defaultCategorySlug(categoryId: string): string {
  const profile = getCategoryProfile(categoryId);
  if (!profile) return slugify(categoryId);
  return profile.id.replace(`${profile.domain}_`, "").replace(/_/g, "-");
}

export function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9가-힣_-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "") || "untitled";
}

export function isCategoryTaxonomyConfig(value: unknown): value is CategoryTaxonomyConfig {
  if (!isRecord(value)) return false;
  if (typeof value.versionId !== "string" || !["draft", "published"].includes(String(value.status))) return false;
  if (!Array.isArray(value.nodes) || value.nodes.length === 0) return false;
  return value.nodes.every((node) => {
    if (!isRecord(node)) return false;
    if (typeof node.id !== "string" || (node.kind !== "group" && node.kind !== "category")) return false;
    if (typeof node.label !== "string" || typeof node.description !== "string" || typeof node.slug !== "string") return false;
    if (typeof node.order !== "number" || !Number.isFinite(node.order)) return false;
    if (!["active", "hidden", "deprecated"].includes(String(node.status))) return false;
    if (node.parentId !== undefined && typeof node.parentId !== "string") return false;
    if (node.profileId !== undefined && typeof node.profileId !== "string") return false;
    if (node.aliases !== undefined && (!Array.isArray(node.aliases) || !node.aliases.every((alias) => typeof alias === "string"))) return false;
    if (node.examples !== undefined && (!Array.isArray(node.examples) || !node.examples.every((example) => typeof example === "string"))) return false;
    return true;
  });
}

export function normalizeCategoryTaxonomyConfig(config?: CategoryTaxonomyConfig | null): CategoryTaxonomyConfig {
  if (!config?.nodes?.length) return DEFAULT_CATEGORY_TAXONOMY_CONFIG;
  const defaultById = new Map(DEFAULT_CATEGORY_TAXONOMY_CONFIG.nodes.map((node) => [node.id, node]));
  const seen = new Set<string>();
  const mergedNodes: CategoryTaxonomyNode[] = [];

  for (const node of config.nodes) {
    if (!node.id || seen.has(node.id)) continue;
    const fallback = defaultById.get(node.id);
    // 과거 실험 필드(카테고리 기본 수수료율)는 문항 설정 rule로 이관돼 제거한다.
    const { defaultUpsellCommissionRate: _legacyRate, ...migratedNode } = migrateTaxonomyNode(node, fallback) as CategoryTaxonomyNode & { defaultUpsellCommissionRate?: string };
    const kind = migratedNode.kind ?? fallback?.kind;
    if (kind !== "group" && kind !== "category") continue;
    const parentId = kind === "category" ? (migratedNode.parentId ?? fallback?.parentId) : undefined;
    mergedNodes.push({
      ...(fallback ?? {}),
      ...migratedNode,
      kind,
      parentId,
      label: migratedNode.label || fallback?.label || migratedNode.id,
      description: migratedNode.description ?? fallback?.description ?? "",
      slug: slugify(migratedNode.slug || fallback?.slug || migratedNode.label || migratedNode.id),
      order: Number.isFinite(migratedNode.order) ? migratedNode.order : fallback?.order ?? 0,
      status: migratedNode.status ?? fallback?.status ?? "active",
      aliases: uniqueStrings(migratedNode.aliases ?? fallback?.aliases ?? []),
      examples: uniqueStrings(migratedNode.examples ?? fallback?.examples ?? []),
    });
    seen.add(node.id);
  }

  for (const fallback of DEFAULT_CATEGORY_TAXONOMY_CONFIG.nodes) {
    if (!seen.has(fallback.id)) mergedNodes.push(fallback);
  }

  return {
    versionId: config.versionId || CATEGORY_TAXONOMY_VERSION_ID,
    status: config.status ?? "draft",
    nodes: sortTaxonomyNodes(mergedNodes),
  };
}

export function sortTaxonomyNodes(nodes: CategoryTaxonomyNode[]): CategoryTaxonomyNode[] {
  return [...nodes].sort((left, right) => {
    const kindRank = left.kind === right.kind ? 0 : left.kind === "group" ? -1 : 1;
    if (kindRank) return kindRank;
    const parentRank = (left.parentId ?? "").localeCompare(right.parentId ?? "");
    if (parentRank) return parentRank;
    return left.order - right.order || left.label.localeCompare(right.label);
  });
}

export function validateCategoryTaxonomy(config: CategoryTaxonomyConfig): CategoryTaxonomyDiagnostic[] {
  const diagnostics: CategoryTaxonomyDiagnostic[] = [];
  const ids = new Set<string>();
  const byId = new Map(config.nodes.map((node) => [node.id, node]));
  for (const node of config.nodes) {
    if (ids.has(node.id)) diagnostics.push({ level: "error", code: "duplicate_node", nodeId: node.id, message: "같은 카테고리 항목이 중복되어 있습니다." });
    ids.add(node.id);
    if (!node.label.trim()) diagnostics.push({ level: "error", code: "empty_label", nodeId: node.id, message: "이름은 비워둘 수 없습니다." });
    if (!node.slug.trim()) diagnostics.push({ level: "error", code: "empty_slug", nodeId: node.id, message: "URL 이름은 비워둘 수 없습니다." });
    if (node.kind === "group" && isReservedCategoryGroupSlug(node.slug)) diagnostics.push({ level: "error", code: "reserved_group_slug", nodeId: node.id, message: `URL 이름에 예약어를 사용할 수 없습니다: /${node.slug}` });
    if (node.kind === "category") {
      if (!node.parentId || !byId.has(node.parentId)) diagnostics.push({ level: "error", code: "missing_parent", nodeId: node.id, message: `${node.label}의 상위 그룹을 찾을 수 없습니다.` });
      if (!node.profileId || !getCategoryProfile(node.profileId)) {
        diagnostics.push({ level: "error", code: "unknown_profile", nodeId: node.id, message: `${node.label}은 신청서 유형과 연결되지 않았습니다.` });
      } else if (node.id !== categoryNodeId(node.profileId as Exclude<IntakeCategoryId, "">)) {
        diagnostics.push({ level: "error", code: "noncanonical_profile_node", nodeId: node.id, message: `${node.label}의 내부 연결 정보가 올바르지 않습니다.` });
      }
    }
    if (node.kind === "group" && node.parentId) diagnostics.push({ level: "error", code: "group_parent", nodeId: node.id, message: "그룹 구조가 올바르지 않습니다." });
  }

  const categoryNodeByProfileId = new Map<string, string>();
  for (const category of config.nodes.filter((node) => node.kind === "category" && node.profileId)) {
    const existingNodeId = categoryNodeByProfileId.get(category.profileId!);
    if (existingNodeId && existingNodeId !== category.id) diagnostics.push({ level: "error", code: "duplicate_profile", nodeId: category.id, message: "같은 신청서 유형에 여러 카테고리가 연결되어 있습니다." });
    categoryNodeByProfileId.set(category.profileId!, category.id);
  }

  const activeGroupSlugs = new Map<string, string>();
  for (const group of config.nodes.filter((node) => node.kind === "group" && node.status === "active")) {
    const existingGroup = activeGroupSlugs.get(group.slug);
    if (existingGroup && existingGroup !== group.id) diagnostics.push({ level: "error", code: "duplicate_group_slug", nodeId: group.id, message: `같은 URL 이름을 쓰는 그룹이 있습니다: /${group.slug}` });
    activeGroupSlugs.set(group.slug, group.id);
  }

  const activeRoutes = new Map<string, string>();
  for (const group of config.nodes.filter((node) => node.kind === "group" && node.status === "active")) {
    for (const category of config.nodes.filter((node) => node.kind === "category" && node.parentId === group.id && node.status === "active")) {
      const slugs = [category.slug, category.profileId ? defaultCategorySlug(category.profileId) : "", ...(category.aliases ?? [])].map(slugify).filter(Boolean);
      for (const categorySlug of slugs) {
        const route = `${group.slug}/${categorySlug}`;
        const existing = activeRoutes.get(route);
        if (existing && existing !== category.id) diagnostics.push({ level: "error", code: "duplicate_route", nodeId: category.id, message: `같은 URL 주소를 쓰는 카테고리가 있습니다: /${route}` });
        activeRoutes.set(route, category.id);
      }
    }
  }

  for (const defaultCategory of DEFAULT_CATEGORY_NODES) {
    const node = config.nodes.find((candidate) => candidate.profileId === defaultCategory.profileId);
    if (!node) diagnostics.push({ level: "warning", code: "missing_default_category", nodeId: defaultCategory.id, message: `${defaultCategory.label} 기본 카테고리가 목록에서 빠져 있습니다.` });
  }

  return dedupeDiagnostics(diagnostics);
}

export function getVisibleCategoryGroups(config?: CategoryTaxonomyConfig | null): TaxonomyCategoryGroup[] {
  const normalized = normalizeCategoryTaxonomyConfig(config);
  const groups = normalized.nodes.filter((node) => node.kind === "group" && node.status === "active");
  const categories = normalized.nodes.filter((node) => node.kind === "category" && node.status === "active" && node.profileId);
  return groups
    .map((group) => {
      const options = categories
        .filter((node) => node.parentId === group.id)
        .sort((left, right) => left.order - right.order || left.label.localeCompare(right.label))
        .flatMap((node) => {
          const profile = getCategoryProfile(node.profileId);
          if (!profile) return [];
          return [{
            id: profile.id,
            domain: profile.domain,
            label: node.label,
            description: node.description || profile.description,
            examples: node.examples?.length ? node.examples : profile.examples,
            nodeId: node.id,
            path: getCategoryPathFromNormalized(profile.id, normalized),
            status: node.status,
          } satisfies TaxonomyCategoryOption];
        });
      return {
        id: group.slug,
        nodeId: group.id,
        slug: group.slug,
        label: group.label,
        description: group.description,
        status: group.status,
        options,
      } satisfies TaxonomyCategoryGroup;
    })
    .filter((group) => group.options.length > 0)
    .sort((left, right) => {
      const leftNode = normalized.nodes.find((node) => node.id === left.nodeId);
      const rightNode = normalized.nodes.find((node) => node.id === right.nodeId);
      return (leftNode?.order ?? 0) - (rightNode?.order ?? 0) || left.label.localeCompare(right.label);
    });
}

export function getAllTaxonomyCategoryIds(config?: CategoryTaxonomyConfig | null): Array<Exclude<IntakeCategoryId, "">> {
  return getVisibleCategoryGroups(config).flatMap((group) => group.options.map((option) => option.id));
}

export function getCategoryDisplayLabel(categoryId: IntakeCategoryId, config?: CategoryTaxonomyConfig | null): string {
  if (!categoryId) return "";
  const normalized = normalizeCategoryTaxonomyConfig(config);
  const node = normalized.nodes.find((candidate) => candidate.kind === "category" && candidate.profileId === categoryId);
  return node?.label ?? getCategoryProfile(categoryId)?.label ?? "";
}

export function getCategoryDisplayBreadcrumb(categoryId: IntakeCategoryId, config?: CategoryTaxonomyConfig | null): string {
  if (!categoryId) return "";
  const normalized = normalizeCategoryTaxonomyConfig(config);
  const category = normalized.nodes.find((candidate) => candidate.kind === "category" && candidate.profileId === categoryId);
  const group = category?.parentId ? normalized.nodes.find((candidate) => candidate.id === category.parentId) : null;
  const label = category?.label ?? getCategoryProfile(categoryId)?.label ?? categoryId;
  return group ? `${group.label} · ${label}` : label;
}

export function getCategoryPath(categoryId: IntakeCategoryId, config?: CategoryTaxonomyConfig | null): string {
  return getCategoryPathFromNormalized(categoryId, normalizeCategoryTaxonomyConfig(config));
}

function getCategoryPathFromNormalized(categoryId: IntakeCategoryId, normalized: CategoryTaxonomyConfig): string {
  if (!categoryId) return "/";
  const category = normalized.nodes.find((node) => node.kind === "category" && node.profileId === categoryId);
  const group = category?.parentId ? normalized.nodes.find((node) => node.id === category.parentId) : null;
  if (!category || !group) {
    const profile = getCategoryProfile(categoryId);
    return profile ? `/${profile.domain}/${defaultCategorySlug(profile.id)}` : "/";
  }
  return `/${group.slug}/${category.slug}`;
}

export function getCategoryIdFromPathname(pathname: string, config?: CategoryTaxonomyConfig | null): IntakeCategoryId {
  const [groupSlug = "", categorySlug = ""] = pathname.split("/").filter(Boolean).map(decodePathSegment);
  if (!groupSlug || !categorySlug) return "";
  const normalized = normalizeCategoryTaxonomyConfig(config);
  const groups = normalized.nodes.filter((node) => node.kind === "group" && node.status === "active" && node.slug === groupSlug);
  for (const group of groups) {
    const category = normalized.nodes.find((node) => {
      if (node.kind !== "category" || node.status !== "active" || node.parentId !== group.id || !node.profileId) return false;
      const slugs = [node.slug, defaultCategorySlug(node.profileId), ...(node.aliases ?? [])].map(slugify);
      return slugs.includes(categorySlug);
    });
    if (category?.profileId) return category.profileId;
  }
  return "";
}

export function createCategoryOptions(config?: CategoryTaxonomyConfig | null): Array<{ value: Exclude<IntakeCategoryId, "">; label: string }> {
  return getVisibleCategoryGroups(config).flatMap((group) => group.options.map((option) => ({ value: option.id, label: `${group.label} · ${option.label}` })));
}

export function getTaxonomyNode(config: CategoryTaxonomyConfig, nodeId: string): CategoryTaxonomyNode | null {
  return normalizeCategoryTaxonomyConfig(config).nodes.find((node) => node.id === nodeId) ?? null;
}

function decodePathSegment(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function migrateTaxonomyNode(node: CategoryTaxonomyNode, fallback?: CategoryTaxonomyNode): CategoryTaxonomyNode {
  if (node.id === "category.beauty_nail" && node.label === "네일" && fallback) {
    return {
      ...node,
      label: fallback.label,
      description: fallback.description,
      examples: fallback.examples,
      aliases: uniqueStrings([...(fallback.aliases ?? []), ...(node.aliases ?? [])]),
    };
  }
  if ((node.id === "category.beauty_semipermanent" || node.id === "category.beauty_other") && node.status === "active") {
    return { ...node, status: "deprecated" };
  }
  return node;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function uniqueStrings(values: string[]): string[] {
  return Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));
}

function dedupeDiagnostics(diagnostics: CategoryTaxonomyDiagnostic[]): CategoryTaxonomyDiagnostic[] {
  const seen = new Set<string>();
  return diagnostics.filter((diagnostic) => {
    const key = `${diagnostic.level}:${diagnostic.code}:${diagnostic.nodeId ?? ""}:${diagnostic.message}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
